import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Building2, FileText, Scale, DollarSign, Users, MapPin, Globe, Briefcase, Linkedin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to construct correct deck URL regardless of stored format
const getDeckUrl = (deckUrl) => {
  if (!deckUrl) return null;
  
  // If it's already an S3 URL or other absolute URL, use it directly
  if (deckUrl.startsWith('https://') && (deckUrl.includes('.s3.') || deckUrl.includes('amazonaws.com'))) {
    return deckUrl;
  }
  
  let deckPath = deckUrl;
  // If it's a full URL from old deployment, extract just the path
  if (deckPath.startsWith('http')) {
    try {
      const url = new URL(deckPath);
      deckPath = url.pathname;
    } catch (e) {
      // Fallback: extract path after domain
      deckPath = '/' + deckPath.split('/').slice(3).join('/');
    }
  }
  // Ensure path uses /api/uploads for ingress routing
  if (deckPath.startsWith('/uploads')) {
    deckPath = deckPath.replace('/uploads', '/api/uploads');
  } else if (!deckPath.startsWith('/api/uploads')) {
    deckPath = '/api/uploads/' + deckPath.split('/').pop();
  }
  return `${process.env.REACT_APP_BACKEND_URL}${deckPath}`;
};

function formatINR(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

// Color palette for investment banker tags
const BANKER_COLORS = [
  { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' },
  { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300' },
  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
];

// Get consistent color for a banker name using improved multi-pass hash
function getBankerColor(bankerName) {
  const name = bankerName.toLowerCase().replace(/\s+/g, '');
  let hash = 0;
  
  // First pass - standard hash
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  
  // Second pass - incorporate position weight
  for (let i = 0; i < name.length; i++) {
    hash = hash ^ (name.charCodeAt(i) * (i + 1) * 31);
  }
  
  // Third factor - length and sum of chars
  const charSum = name.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0);
  hash = hash ^ (charSum * name.length);
  
  return BANKER_COLORS[Math.abs(hash) % BANKER_COLORS.length];
}

export default function DealDetailPage() {
  const navigate = useNavigate();
  const { dealId } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commitmentAmount, setCommitmentAmount] = useState('');
  const [committing, setCommitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const email = localStorage.getItem('user_email');
    if (!email) {
      navigate('/login');
      return;
    }

    fetchDeal();
  }, [dealId, navigate]);

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}`);
      setDeal(response.data);
    } catch (error) {
      toast.error('Failed to load deal');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!commitmentAmount || parseInt(commitmentAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setCommitting(true);
    try {
      const email = localStorage.getItem('user_email');
      await axios.post(`${API}/commitments?email=${email}`, {
        deal_id: dealId,
        amount_inr: parseInt(commitmentAmount)
      });
      
      toast.success('Capital committed successfully!');
      setDialogOpen(false);
      setCommitmentAmount('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to commit';
      toast.error(message);
    } finally {
      setCommitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <p className="text-[#64748B]">Loading deal...</p>
      </div>
    );
  }

  if (!deal) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#64748B] hover:text-[#0F172A] px-0"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Deals
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Deal Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="h-8 w-8 text-brand" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-[#0F172A] mb-2">{deal.brand_name || deal.name}</h1>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-brand text-white">{deal.type}</Badge>
                  {/* Entity Tags (Investment Bankers, Funds, Angel Networks) */}
                  {deal.investment_bankers?.map((banker) => {
                    const color = getBankerColor(banker);
                    return (
                      <span key={`ib-${banker}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text} border ${color.border}`}>
                        {banker}
                      </span>
                    );
                  })}
                  {deal.funds?.map((fund) => {
                    const color = getBankerColor(fund);
                    return (
                      <span key={`fund-${fund}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text} border ${color.border}`}>
                        {fund}
                      </span>
                    );
                  })}
                  {deal.angel_networks?.map((network) => {
                    const color = getBankerColor(network);
                    return (
                      <span key={`an-${network}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.bg} ${color.text} border ${color.border}`}>
                        {network}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <p className="text-lg text-[#64748B]">{deal.one_liner || deal.description}</p>
        </div>

        {/* Amount Raising */}
        <Card className="border-[#E2E8F0] shadow-none mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              {deal.amount_raising > 0 && (
                <div>
                  <p className="text-sm text-[#64748B] mb-1">Amount Raising</p>
                  <p className="font-heading text-3xl font-bold text-[#10B981]">{formatINR(deal.amount_raising)}</p>
                </div>
              )}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="commit-capital-btn"
                    className="bg-[#10B981] hover:bg-[#059669] text-white h-12 px-8 font-semibold"
                  >
                    <DollarSign className="mr-2 h-5 w-5" />
                    Commit Capital
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                      COMMIT CAPITAL
                    </DialogTitle>
                    <DialogDescription className="text-[#64748B]">
                      Enter the amount you wish to commit to {deal.brand_name || deal.name}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-[#0F172A] font-medium">Amount (INR)</Label>
                      <Input
                        data-testid="commitment-amount-input"
                        type="number"
                        placeholder="Enter amount"
                        value={commitmentAmount}
                        onChange={(e) => setCommitmentAmount(e.target.value)}
                        className="bg-white border-[#E2E8F0] h-12 text-lg"
                        autoFocus
                      />
                    </div>
                    <Button
                      data-testid="confirm-commit-btn"
                      onClick={handleCommit}
                      disabled={committing}
                      className="w-full bg-[#10B981] hover:bg-[#059669] text-white h-12 font-semibold"
                    >
                      {committing ? 'Committing...' : 'Confirm Commitment'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Deal Sections */}
        <div className="grid gap-6">
          {/* Description */}
          {deal.description && (
          <Card className="border-[#E2E8F0] shadow-none">
            <CardHeader>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-brand" />
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">DESCRIPTION</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap">{deal.description}</p>
            </CardContent>
          </Card>
          )}

          {/* Stage, Category, Location, Website - Company Details */}
          {(deal.stage || deal.category || deal.location || deal.website) && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-brand" />
                  <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">COMPANY DETAILS</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {deal.stage && (
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Stage</p>
                      <p className="font-medium text-[#0F172A]">{deal.stage}</p>
                    </div>
                  )}
                  {deal.category && (
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Category</p>
                      <p className="font-medium text-[#0F172A]">{deal.category}</p>
                    </div>
                  )}
                  {deal.location && (
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Location</p>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-[#64748B]" />
                        <p className="font-medium text-[#0F172A]">{deal.location}</p>
                      </div>
                    </div>
                  )}
                  {deal.website && (
                    <div>
                      <p className="text-sm text-[#64748B] mb-1">Website</p>
                      <a 
                        href={deal.website.startsWith('http') ? deal.website : `https://${deal.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-brand hover:underline"
                      >
                        <Globe className="h-4 w-4" />
                        <span className="truncate">{deal.website.replace(/^https?:\/\//, '')}</span>
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Founders Section */}
          {deal.founders && deal.founders.length > 0 && deal.founders.some(f => f.name) && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-brand" />
                  <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">FOUNDERS</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {deal.founders.filter(f => f.name).map((founder, idx) => (
                    <div key={idx} className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0] flex items-center justify-between">
                      <p className="font-semibold text-[#0F172A]">{founder.name}</p>
                      {founder.linkedin && (
                        <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-brand hover:underline">
                          <Linkedin className="h-4 w-4" />
                          <span>LinkedIn</span>
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Product Information */}
          {deal.product && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-brand" />
                  <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">PRODUCT</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap">{deal.product}</p>
              </CardContent>
            </Card>
          )}

          {/* Product Pricing */}
          {deal.product_pricing && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-brand" />
                  <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">PRODUCT PRICING</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[#0F172A] leading-relaxed whitespace-pre-wrap">{deal.product_pricing}</p>
              </CardContent>
            </Card>
          )}

          {/* Previous Fundraises */}
          {deal.previous_fundraises && deal.previous_fundraises.length > 0 && deal.previous_fundraises.some(f => f.round || f.amount || f.investors) && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-brand" />
                  <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">PREVIOUS FUNDRAISES</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {deal.previous_fundraises.filter(f => f.round || f.amount || f.investors).map((fundraise, idx) => (
                    <div key={idx} className="p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                      <div className="grid grid-cols-3 gap-4">
                        {fundraise.round && (
                          <div>
                            <p className="text-xs text-[#64748B]">Round</p>
                            <p className="font-medium text-[#0F172A]">{fundraise.round}</p>
                          </div>
                        )}
                        {fundraise.amount && (
                          <div>
                            <p className="text-xs text-[#64748B]">Amount</p>
                            <p className="font-medium text-[#0F172A]">{fundraise.amount}</p>
                          </div>
                        )}
                        {fundraise.investors && (
                          <div>
                            <p className="text-xs text-[#64748B]">Angels</p>
                            <p className="font-medium text-[#0F172A]">{fundraise.investors}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Terms - Combined view for terms_type, valuation, safe_terms, other_terms */}
          {(deal.terms_type || deal.valuation_amount || deal.safe_terms || deal.other_terms) && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-brand" />
                  <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">TERMS</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {deal.terms_type && (
                  <div>
                    <p className="text-sm text-[#64748B] mb-1">Terms Type</p>
                    <Badge className="bg-[#F1F5F9] text-[#0F172A]">{deal.terms_type}</Badge>
                  </div>
                )}
                {deal.terms_type === 'Valuation' && deal.valuation_amount && (
                  <div>
                    <p className="text-sm text-[#64748B] mb-1">Valuation</p>
                    <p className="font-medium text-[#0F172A]">{formatINR(deal.valuation_amount)}</p>
                  </div>
                )}
                {deal.terms_type === 'SAFE' && deal.safe_terms && (
                  <div>
                    <p className="text-sm text-[#64748B] mb-1">SAFE Terms</p>
                    <p className="text-[#0F172A] whitespace-pre-wrap">{deal.safe_terms}</p>
                  </div>
                )}
                {deal.terms_type === 'Other' && deal.other_terms && (
                  <div>
                    <p className="text-sm text-[#64748B] mb-1">Other Terms</p>
                    <p className="text-[#0F172A] whitespace-pre-wrap">{deal.other_terms}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Deck */}
          {deal.deck_url && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">PITCH DECK</CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href={getDeckUrl(deal.deck_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline"
                >
                  View Deck
                </a>
              </CardContent>
            </Card>
          )}

          {/* Demo Link */}
          {deal.demo_link && (
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A]">DEMO</CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href={deal.demo_link.startsWith('http') ? deal.demo_link : `https://${deal.demo_link}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand hover:underline"
                >
                  View Demo →
                </a>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
